import React from "react";

type Availability = {
  bank: string;
  overall: {
    min_value_date: string | null;
    max_value_date: string | null;
    count: number;
  };
  by_op_type: any[];
  by_asset_type: any[];
  by_asset_name: any[];
  by_account: any[];
};

type AvailabilityStatus = "ok" | "stale" | "old" | "missing";

function getAvailabilityStatus(av: Availability): {
  status: AvailabilityStatus;
  label: string;
} {
  const maxDate = av.overall.max_value_date;
  const count = av.overall.count ?? 0;
  if (!maxDate || count === 0) return { status: "missing", label: "Mancante" };
  const d = new Date(maxDate);
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 2) return { status: "ok", label: "Aggiornato" };
  if (days <= 7) return { status: "stale", label: `${days}g fa` };
  return { status: "old", label: `${days}g fa` };
}

function formatRange(av: Availability) {
  const { min_value_date, max_value_date } = av.overall;
  if (!min_value_date && !max_value_date) return "N/D";
  const fmt = (d: string | null) =>
    d
      ? new Date(d).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
      : "—";
  return `${fmt(min_value_date)} → ${fmt(max_value_date)}`;
}

export function AvailabilityNavCard({
  availability,
  bankLabel,
  bankLogo,
  isActive = false,
  onClick,
}: {
  availability: Availability;
  bankLabel: string;
  bankLogo?: string;
  isActive?: boolean;
  onClick?: () => void;
}) {
  const status = getAvailabilityStatus(availability);
  const range = formatRange(availability);

  const statusColors: Record<AvailabilityStatus, string> = {
    ok: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    stale: "bg-amber-50 text-amber-700 ring-amber-200",
    old: "bg-rose-50 text-rose-700 ring-rose-200",
    missing: "bg-slate-50 text-slate-700 ring-slate-200",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-3 rounded-lg border transition",
        "px-3 py-2",
        isActive ? "bg-blue-50 border-blue-200 ring-1 ring-blue-100" : "border-slate-200 bg-white hover:bg-slate-50",
      ].join(" ")}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white overflow-hidden">
        {bankLogo ? (
          <img
            src={bankLogo}
            alt={bankLabel}
            className="h-6 w-6 object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <span className="text-[10px] font-semibold text-slate-500">{bankLabel.slice(0, 3).toUpperCase()}</span>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 min-w-0">
        <div className="min-w-0 mr-1">
          <div className="flex items-center gap-1">
            <p className="truncate text-sm font-medium text-slate-900">{bankLabel}</p>
          </div>
          <p className="text-[11px] text-slate-500 leading-tight">{range}</p>
        </div>

        <div className="hidden sm:flex flex-col items-end gap-1">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${statusColors[status.status]}`}>
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-current/70" aria-hidden />
            {status.label}
          </span>
          {/* <span className="text-[11px] text-slate-500">{availability.overall.count.toLocaleString("it-IT")} righe</span> */}
        </div>
      </div>
    </button>
  );
}


